import React, { useState, useEffect } from 'react';
import { apiClient } from '../../App';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Zap, Loader2, Droplets, Beef, Footprints, Moon, Salad, CandyOff, Dumbbell, CookingPot, ClipboardList, Brain } from 'lucide-react';

const iconOptions = [
  { value: 'droplets', label: 'Water', Icon: Droplets },
  { value: 'beef', label: 'Protein', Icon: Beef },
  { value: 'footprints', label: 'Walking', Icon: Footprints },
  { value: 'moon', label: 'Sleep', Icon: Moon },
  { value: 'salad', label: 'Vegetables', Icon: Salad },
  { value: 'candy-off', label: 'No Sugar', Icon: CandyOff },
  { value: 'dumbbell', label: 'Workout', Icon: Dumbbell },
  { value: 'cooking-pot', label: 'Meal Prep', Icon: CookingPot },
  { value: 'clipboard-list', label: 'Tracking', Icon: ClipboardList },
  { value: 'brain', label: 'Mindfulness', Icon: Brain },
];

const AdminActions = () => {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [form, setForm] = useState({
    name: '',
    description: '',
    icon: 'droplets',
    points: 1
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const actionsRes = await apiClient.get('/actions');
      setActions(actionsRes.data);
    } catch (err) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      name: '',
      description: '',
      icon: 'droplets',
      points: 1
    });
    setEditing(null);
  };

  const openEdit = (action) => {
    setForm({
      name: action.name,
      description: action.description,
      icon: action.icon,
      points: action.points
    });
    setEditing(action.id);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) {
      toast.error('Please enter an action name');
      return;
    }

    setSaving(true);
    try {
      const payload = { ...form, challenge_id: null };
      if (editing) {
        await apiClient.put(`/actions/${editing}`, payload);
        toast.success('Action updated');
      } else {
        await apiClient.post('/actions', payload);
        toast.success('Action created');
      }
      fetchData();
      setDialogOpen(false);
      resetForm();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save action');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this action?')) return;
    
    try {
      await apiClient.delete(`/actions/${id}`);
      toast.success('Action deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete action');
    }
  };

  const getIconComponent = (iconName) => {
    const iconOption = iconOptions.find(o => o.value === iconName);
    return iconOption ? iconOption.Icon : Zap;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 md:p-8 max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-stone-900">ACTIONS</h1>
            <p className="text-stone-600 text-sm">Define daily actions users can check off for points</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#EFB342] text-black hover:bg-[#D99F30] font-bold" data-testid="add-action-btn">
                <Plus size={18} className="mr-2" />
                Add Action
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-stone-200 text-stone-900 max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">
                  {editing ? 'Edit Action' : 'Create Action'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div>
                  <Label>Name *</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Drink 2L Water"
                    className="bg-stone-50 border-stone-200 mt-1"
                    data-testid="action-name-input"
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Stay hydrated by drinking at least 2 litres of water today."
                    className="bg-stone-50 border-stone-200 mt-1"
                    rows={2}
                    data-testid="action-description-input"
                  />
                  <p className="text-xs text-stone-500 mt-1">
                    Tip: wrap words with <code className="px-1 rounded bg-stone-100 text-stone-700">**double asterisks**</code> to make them <strong>bold</strong>. URLs auto-link.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Icon</Label>
                    <Select value={form.icon} onValueChange={(v) => setForm({ ...form, icon: v })}>
                      <SelectTrigger className="bg-stone-50 border-stone-200 mt-1" data-testid="action-icon-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-stone-200">
                        {iconOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>
                            <div className="flex items-center gap-2">
                              <opt.Icon size={16} />
                              {opt.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Points</Label>
                    <Input
                      type="number"
                      min="1"
                      max="5"
                      value={form.points}
                      onChange={(e) => setForm({ ...form, points: parseInt(e.target.value) || 1 })}
                      className="bg-stone-50 border-stone-200 mt-1"
                      data-testid="action-points-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={saving}
                  className="w-full bg-[#EFB342] text-black hover:bg-[#D99F30] font-bold"
                  data-testid="action-submit-btn"
                >
                  {saving ? <Loader2 className="animate-spin" size={18} /> : editing ? 'Update' : 'Create'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Actions Grid */}
        <div className="grid md:grid-cols-2 gap-4">
          {actions.length > 0 ? (
            actions.map((action) => {
              const IconComponent = getIconComponent(action.icon);
              return (
                <div
                  key={action.id}
                  className="bg-white border border-stone-200 rounded-2xl p-5 hover:border-stone-300 transition-colors"
                  data-testid={`action-${action.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#EFB342]/20 rounded-xl flex items-center justify-center flex-shrink-0">
                        <IconComponent className="text-[#EFB342]" size={24} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-stone-900">{action.name}</h3>
                        <p className="text-stone-600 text-sm mt-1 line-clamp-2">{action.description}</p>
                        <div className="flex items-center gap-3 mt-2">
                          <span className="px-2 py-0.5 bg-[#EFB342]/20 text-[#EFB342] text-xs rounded-full font-medium">
                            +{action.points} pt{action.points > 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEdit(action)}
                        className="text-stone-600 hover:text-stone-900 h-8 w-8"
                        data-testid={`edit-action-${action.id}`}
                      >
                        <Pencil size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(action.id)}
                        className="text-stone-600 hover:text-red-500 h-8 w-8"
                        data-testid={`delete-action-${action.id}`}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="col-span-2 text-center py-16 bg-white border border-stone-200 rounded-2xl">
              <Zap className="mx-auto text-stone-400 mb-4" size={48} />
              <p className="text-stone-500">No actions defined yet</p>
              <p className="text-stone-400 text-sm">Create actions for users to check off daily</p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default AdminActions;
